import { z } from 'zod';
import { forgeV1 } from './forge';
import { inferenceV1 } from './inference';
import { realtimeV1 } from './realtime';
import { roomsV1 } from './rooms';

export * from './forge';
export * from './inference';
export * from './realtime';
export * from './rooms';

/**
 * The core contracts plugins provide (`crowdex.json` `provides`). The core calls a provider app
 * with `POST /contracts/<contract>/<operation>` and a JSON body; the app answers the operation's
 * output, or `{ error: { code, message, retryable } }`. Both sides validate with these schemas.
 */
export const PROVIDER_CONTRACTS = {
  'forge.v1': forgeV1,
  'inference.v1': inferenceV1,
  'realtime.v1': realtimeV1,
  'rooms.v1': roomsV1,
} as const;

export type ProviderContracts = typeof PROVIDER_CONTRACTS;
export type ProviderContract = keyof ProviderContracts;
export type ProviderOperation<C extends ProviderContract> = keyof ProviderContracts[C] & string;
type Schema<
  C extends ProviderContract,
  O extends ProviderOperation<C>,
  K extends 'input' | 'output',
> = ProviderContracts[C][O] extends Record<K, infer S extends z.ZodType> ? S : never;
/** What a caller passes to an operation. */
export type ProviderInput<C extends ProviderContract, O extends ProviderOperation<C>> = z.input<
  Schema<C, O, 'input'>
>;
/** What a caller gets from an operation. */
export type ProviderOutput<C extends ProviderContract, O extends ProviderOperation<C>> = z.output<
  Schema<C, O, 'output'>
>;

/** The input and output schemas of an operation; undefined for an unknown one. */
export function providerOperation(
  contract: string,
  operation: string,
): { input: z.ZodType; output: z.ZodType } | undefined {
  const operations = (PROVIDER_CONTRACTS as Record<string, Record<string, unknown>>)[contract];
  const definition =
    operations !== undefined && Object.hasOwn(operations, operation)
      ? operations[operation]
      : undefined;
  return definition as { input: z.ZodType; output: z.ZodType } | undefined;
}

/** The route the core calls on a provider app. */
export function providerRoute(contract: string, operation: string): string {
  return `/contracts/${encodeURIComponent(contract)}/${encodeURIComponent(operation)}`;
}

export const PROVIDER_ERROR_CODES = [
  'invalid_request',
  'unauthorized',
  'not_found',
  'conflict',
  'unconfigured',
  'unavailable',
  'internal_error',
] as const;
export type ProviderErrorCode = (typeof PROVIDER_ERROR_CODES)[number];

export const providerErrorSchema = z
  .object({
    error: z
      .object({
        code: z.enum(PROVIDER_ERROR_CODES),
        message: z.string().max(2000),
        retryable: z.boolean().default(false),
      })
      .strict(),
  })
  .strict();

/** A provider's refusal: thrown by a provider's handler, answered as `{ error }`. */
export class ProviderError extends Error {
  constructor(
    readonly code: ProviderErrorCode,
    message: string,
    readonly retryable = false,
  ) {
    super(message);
    this.name = 'ProviderError';
  }
}

const STATUS: Record<ProviderErrorCode, number> = {
  invalid_request: 400,
  unauthorized: 403,
  not_found: 404,
  conflict: 409,
  unconfigured: 424,
  unavailable: 503,
  internal_error: 500,
};

/** A provider's operations: each takes its validated input and answers its output. */
export type ProviderHandlers<C extends ProviderContract> = {
  [O in ProviderOperation<C>]?: (
    input: z.output<Schema<C, O, 'input'>>,
    request: Request,
  ) => z.input<Schema<C, O, 'output'>> | Promise<z.input<Schema<C, O, 'output'>>>;
};

/**
 * The `POST /contracts/<contract>/<operation>` routes of a provider app: each request validated
 * against its operation's input, each answer against its output.
 */
export function serveProvider<C extends ProviderContract>(
  contract: C,
  handlers: ProviderHandlers<C>,
): (request: Request) => Promise<Response | null> {
  return async (request) => {
    const prefix = `/contracts/${contract}/`;
    const { pathname } = new URL(request.url);
    if (!pathname.startsWith(prefix)) return null;
    const operation = decodeURIComponent(pathname.slice(prefix.length));
    const definition = providerOperation(contract, operation);
    const handler = (
      handlers as Record<string, ((input: unknown, request: Request) => unknown) | undefined>
    )[operation];
    const failure = (error: ProviderError) =>
      Response.json(
        { error: { code: error.code, message: error.message, retryable: error.retryable } },
        { status: STATUS[error.code] },
      );
    if (request.method !== 'POST') return new Response('Method not allowed', { status: 405 });
    if (definition === undefined || handler === undefined)
      return failure(new ProviderError('not_found', `${contract} has no operation ${operation}.`));
    const input = definition.input.safeParse(await request.json().catch(() => undefined));
    if (!input.success)
      return failure(new ProviderError('invalid_request', z.prettifyError(input.error)));
    try {
      const output = definition.output.parse(await handler(input.data, request));
      return Response.json(output);
    } catch (error) {
      if (error instanceof ProviderError) return failure(error);
      if (error instanceof z.ZodError)
        return failure(
          new ProviderError('internal_error', `The provider answered an invalid ${operation}.`),
        );
      return failure(
        new ProviderError(
          'unavailable',
          error instanceof Error ? error.message : String(error),
          true,
        ),
      );
    }
  };
}
